import {
  IBusinessOperation,
  IESGDetails,
  IGoalsAndOperationalPlans,
  ISummarizeReport,
} from '@Domains/Progress';
import { EditReportFormValues } from '@Domains/Reports';
import { getBuddhistYear } from '@Utils/datetime';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/th';
import { ReportType, Status } from 'src/Enum/Report';

dayjs.locale('th');

export interface IProgress {
  title: string;
  progress?: number;
  path: string;
  disabled?: boolean;
}

export const stepsPublishReportCVUP = [
  { number: '1', label: 'CGR Score' },
  { number: '2', label: 'ขั้นตอนกรอกข้อมูล Corporate Value Up' },
  { number: '3', label: 'ขั้นตอนการนำส่งรายงาน' },
  { number: '4', label: 'เผยแพร่รายงาน' },
];

export const getStatusFromProgress = (progress: number): Status => {
  if (progress === 100) {
    return Status.PASS;
  } else if (progress < 100) {
    return Status.IN_PROGRESS;
  }
  throw new Error('Invalid progress value');
};

export const getCurrentDate = () => {
  return dayjs().format('D MMM YYYY');
};

export const progressStepFromBoardDirectorData = (
  score: number,
  summarizeReport?: ISummarizeReport
): IProgress[] => {
  return [
    {
      title: 'ตรวจสอบข้อมูล',
      path: 'review-summary-report',
      progress: summarizeReport?.isDraftReport ? 100 : 0,
    },
    {
      title: 'บันทึกวันที่มีมติจากคณะกรรมการบริษัท',
      path: 'review-summary-report',
      disabled: score < 90,
      progress: summarizeReport?.committeeApprovalDate ? 100 : 0,
    },
  ];
};

export const progressESGUpgradePlanData = (esg?: IESGDetails): IProgress[] => {
  return [
    {
      title: 'เปิดเผยข้อมูลจาก ESG Data Platform',
      path: 'platform',
      progress: esg?.esgDataPlatform?.isSectionCompletedTh ? 100 : 0,
    },
    {
      title: 'เปิดเผยข้อมูลเกี่ยวข้องกับการปล่อยก๊าซเรือนกระจก',
      path: 'greenhouse',
      progress: esg?.greenhouse?.completionPercentage,
    },
  ];
};

export const progressNatureOfBusinessOperationsData = (
  businessOperation?: IBusinessOperation
): IProgress[] => [
  {
    title: 'ลักษณะการประกอบธุรกิจ',
    path: 'nature-of-business',
    progress: businessOperation?.natureOfBusiness.completionPercentage,
  },
  {
    title: 'การจัดการผลกระทบต่อผู้มีส่วนได้เสียในห่วงโซ่คุณค่าของธุรกิจ',
    path: 'impacts-on-stakeholders-management',
    progress:
      businessOperation?.impactsOnStakeholdersManagement.completionPercentage,
  },
  {
    title: 'ข้อมูลทางการเงินที่สำคัญ',
    path: 'financial-statements',
    progress: businessOperation?.financialStatements.completionPercentage,
  },
];

export const progressTargetGroupAndPlanData = (
  goalsAndOperationalPlans?: IGoalsAndOperationalPlans
): IProgress[] => [
  {
    title: 'แผนการเพิ่มมูลค่าองค์กร และการบริหารความเสี่ยง',
    path: 'goals-and-operational-plans',
    progress:
      goalsAndOperationalPlans?.goalsOperationalPlansAndRiskManagement
        .completionPercentage,
  },
];

export const durationYearOptions = [
  { label: 'ปี', value: ReportType.SINGLE },
  { label: 'ช่วงปี', value: ReportType.RANGE },
];

export const mapPayloadEditReport = (data: EditReportFormValues) => {
  return {
    startOperatingYear: getBuddhistYear(data.startOperatingYear?.year()),
    endOperatingYear: data.endOperatingYear
      ? getBuddhistYear(data.endOperatingYear.year())
      : null,
    reportType: data.reportType,
  };
};

export const useSubmitDisabled = (
  reportType: ReportType,
  startYear: Dayjs | null,
  endYear: Dayjs | null
) => {
  if (reportType === ReportType.SINGLE) {
    return !startYear;
  }
  if (reportType === ReportType.RANGE) {
    return !startYear || !endYear;
  }
  return false;
};
